import React from "react";
import type { Item } from "@/lib/types";
import ItemCard from "@/components/ui/ItemCard";
import type { useItemShareCard } from "@/lib/hooks/useItemShareCard";
import type { ProcessedItem } from "@/lib/market/types";

interface MarketItemsGridProps {
  filteredItems: Item[];
  viewMode: "grid" | "list";
  locationEndBannerInsertIndex: number | null;
  itemShare: ReturnType<typeof useItemShareCard>;
  isMounted: boolean;
  /** When set, a card click opens the quick-view modal instead of navigating. */
  onItemClick?: (item: Item) => void;
}

function LocationEndBanner({ viewMode }: { viewMode: "grid" | "list" }) {
  return (
    <div
      className={
        viewMode === "grid"
          ? "col-span-1 md:col-span-2 lg:col-span-3"
          : "w-full"
      }
    >
      <div className="rounded-lg border border-primary/30 bg-primary/10 px-4 py-6">
        <p className="text-sm font-semibold text-foreground">
          End of items near you
        </p>
        <p className="text-xs text-text-light mt-1">
          From here on, listings are from other locations.
        </p>
      </div>
    </div>
  );
}

const MarketItemsGrid = React.memo(
  ({
    filteredItems,
    viewMode,
    locationEndBannerInsertIndex,
    itemShare,
    isMounted,
    onItemClick,
  }: MarketItemsGridProps) => {
    if (isMounted) {
      return (
        <div
          className={
            viewMode === "grid"
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              : "space-y-4"
          }
        >
          {(() => {
            const nodes: React.ReactNode[] = [];
            for (let i = 0; i < filteredItems.length; i += 1) {
              if (
                locationEndBannerInsertIndex != null &&
                i === locationEndBannerInsertIndex
              ) {
                nodes.push(
                  <React.Fragment key="__location-end__">
                    <LocationEndBanner viewMode={viewMode} />
                  </React.Fragment>,
                );
              }

              const item = filteredItems[i];
              const processedItem = item as ProcessedItem;
              nodes.push(
                <ItemCard
                  key={item.id}
                  item={item}
                  viewMode={viewMode}
                  onClick={onItemClick ? () => onItemClick(item) : undefined}
                  distance={processedItem.distance}
                  disableDistanceAutoCalculation={true}
                  itemShareUrl={
                    itemShare.showItemShare
                      ? itemShare.buildItemShareUrl(item.id) || undefined
                      : undefined
                  }
                  partnerShareToolsEnabled={
                    itemShare.showItemShare &&
                    itemShare.partnerShareToolsEnabled
                  }
                />,
              );
            }
            return nodes;
          })()}
        </div>
      );
    }

    // Show a placeholder grid during server rendering and hydration
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {(() => {
          const nodes: React.ReactNode[] = [];
          for (let i = 0; i < filteredItems.length; i += 1) {
            if (
              locationEndBannerInsertIndex != null &&
              i === locationEndBannerInsertIndex
            ) {
              nodes.push(
                <div
                  key="__location-end__"
                  className="col-span-1 md:col-span-2 lg:col-span-3"
                >
                  <div className="rounded-lg border border-primary/30 bg-primary/10 px-4 py-6">
                    <p className="text-sm font-semibold text-foreground">
                      End of items near you
                    </p>
                    <p className="text-xs text-text-light mt-1">
                      From here on, listings are from other locations.
                    </p>
                  </div>
                </div>,
              );
            }

            const item = filteredItems[i];
            const processedItem = item as ProcessedItem;
            nodes.push(
              <ItemCard
                key={item.id}
                item={item}
                viewMode="grid"
                onClick={onItemClick ? () => onItemClick(item) : undefined}
                distance={processedItem.distance}
                disableDistanceAutoCalculation={true}
                itemShareUrl={
                  itemShare.showItemShare
                    ? itemShare.buildItemShareUrl(item.id) || undefined
                    : undefined
                }
                partnerShareToolsEnabled={
                  itemShare.showItemShare && itemShare.partnerShareToolsEnabled
                }
              />,
            );
          }
          return nodes;
        })()}
      </div>
    );
  },
);
MarketItemsGrid.displayName = "MarketItemsGrid";

export default MarketItemsGrid;
